import type { ris, startseite, wahl } from "@progressive-development/politik-model";

import { BEZIRKE } from "../../config/bezirke.js";
import { sortiereSitze } from "../home/sitzverteilung.js";
import { kennzahlenVon } from "./gremien-kennzahlen.js";

/**
 * Was die Übersicht „Rat, Gremien und Verwaltung" und der Startseiten-Block
 * aus den Verzeichnissen ablesen — reine Funktionen
 * (FACHKONZEPT-gremien-uebersicht.md, Teil B 10).
 *
 * Es wird nichts gerechnet, was die Quellen nicht sagen: Zahlen sind
 * Anzahlen aus den Dokumenten (Sitzplan, Gremienliste, Verteilungsplan,
 * Personalbericht), Reihenfolgen sind die der Quellen (Sitze absteigend beim
 * Sitzplan, amtliche Nummern bei den Bezirken). Fehlt ein Dokument, fehlt
 * die Zahl — kein Kasten wird mit 0 gefüllt.
 */

const TYP_BEZIRKSVERTRETUNG = "bezirksvertretung";

/** Die Zahlen des Organigramms — jede fehlt, wenn ihr Dokument fehlt. */
export interface OrganigrammZahlen {
  ratSitze?: number;
  bezirksvertretungen?: number;
  /** Sitze je Bezirksvertretung, wenn für alle gleich (heute 19). */
  bvSitze?: number;
  ausschuesse?: number;
  dezernate?: number;
  aemter?: number;
  beschaeftigte?: number;
  /** Stichtag der Beschäftigtenzahl (ISO) — der Hinweis der Quelle steht auf /dezernate. */
  beschaeftigteStichtag?: string;
  /**
   * Anteile je Dezernat an der Summe der Dezernate — die vier größten absteigend,
   * dann der Rest als ein Segment. Für den Balken im Organigramm; fehlt, wenn
   * kein Dezernat eine Zahl hat.
   */
  beschaeftigteAnteile?: number[];
  oberbuergermeister?: { rolle: string; name: string; hinweis?: string };
  buergermeisterinnen?: number;
  /** Die Kräfte im Rat nach dem Sitzplan — Fraktionen, Gruppen, Einzelmandate. */
  kraefteImRat?: { fraktionen: number; gruppen: number; einzelmandate: number };
  /** Parteien und Wählergruppen mit mindestens einem Sitz in einer Bezirksvertretung. */
  parteienInBven?: number;
}

export interface Verzeichnisse {
  gremien?: startseite.GremiumEintrag[];
  sitzverteilung?: startseite.SitzverteilungDokument;
  gremienDerStadt?: startseite.GremienDerStadtDokument;
  dezernate?: startseite.DezernateDokument;
}

export function organigrammZahlen(
  v: Verzeichnisse,
  bvSitzverteilung?: wahl.BvSitzverteilungDokument | undefined,
): OrganigrammZahlen {
  // Die Sitze je BV sind für alle gleich (Wahlverfahren) — der erste Bezirk genügt.
  const bvSitze = bvSitzverteilung?.bezirke?.[0]?.sitzeGesamt;
  // Gewählte Kräfte in den Bezirksvertretungen: Slugs aus den neun Sitzverteilungen, ohne Doppelzählung.
  const inBven = new Set<string>();
  for (const b of bvSitzverteilung?.bezirke ?? [])
    for (const a of b.verteilung ?? []) if (a.partei && (a.sitze ?? 0) > 0) inBven.add(a.partei);
  const bven = (v.gremien ?? []).filter((g) => g.typ === TYP_BEZIRKSVERTRETUNG);
  const sv = v.sitzverteilung;
  return {
    ratSitze: sv?.sitzeGesamt,
    bezirksvertretungen: bven.length > 0 ? bven.length : undefined,
    bvSitze,
    ausschuesse: v.gremienDerStadt?.gremien.length,
    dezernate: v.dezernate
      ? (v.dezernate.anzahl.dezernate ?? v.dezernate.dezernate.length)
      : undefined,
    aemter: v.dezernate?.anzahl.aemter,
    beschaeftigte: v.dezernate?.beschaeftigte?.anzahl,
    beschaeftigteStichtag: v.dezernate?.beschaeftigte?.stichtag,
    beschaeftigteAnteile: beschaeftigteAnteile(v.dezernate),
    oberbuergermeister: sv?.vorsitz,
    buergermeisterinnen: sv?.buergermeisterinnen?.length,
    parteienInBven: inBven.size > 0 ? inBven.size : undefined,
    kraefteImRat: sv?.fraktionen?.length
      ? {
          fraktionen: sv.fraktionen.filter((f) => f.status === "fraktion").length,
          gruppen: sv.fraktionen.filter((f) => f.status === "gruppe").length,
          einzelmandate: sv.fraktionen.filter((f) => f.status === "einzelmandat").length,
        }
      : undefined,
  };
}

/**
 * Beschäftigte je Dezernat als Anteile an der Summe der Dezernate: die vier
 * größten absteigend, der Rest zusammengefasst — genug für ein Balkenbild,
 * das die Verwaltung gliedert, ohne zehn Segmente zu beschriften.
 */
export function beschaeftigteAnteile(
  dok: startseite.DezernateDokument | undefined,
): number[] | undefined {
  const zahlen = (dok?.dezernate ?? [])
    .map((d) => d.beschaeftigte)
    .filter((n): n is number => typeof n === "number" && n > 0);
  const summe = zahlen.reduce((a, b) => a + b, 0);
  if (zahlen.length === 0 || summe === 0) return undefined;
  const sortiert = [...zahlen].sort((a, b) => b - a);
  const groesste = sortiert.slice(0, 4);
  const rest = sortiert.slice(4).reduce((a, b) => a + b, 0);
  return [...groesste, ...(rest > 0 ? [rest] : [])].map((n) => n / summe);
}

/** Eine Kraft im Rat, wie der Sitzplan sie führt. */
export interface FraktionImRat {
  partei: string;
  /** Wortlaut der Quelle. */
  name: string;
  sitze: number;
  status: startseite.SitzEintrag["status"];
}

/** Die Kräfte des Sitzplans: Fraktionen nach Sitzen, dann Gruppen und Einzelmandate (Regel `sortiereSitze`). */
export function fraktionenImRat(
  sv: startseite.SitzverteilungDokument | undefined,
): FraktionImRat[] {
  return sortiereSitze(sv?.fraktionen ?? []).map((e) => ({
    partei: e.partei,
    name: e.name,
    sitze: e.sitze,
    status: e.status,
  }));
}

/** Eine Zeile der neun Bezirksvertretungen. */
export interface BvZeile {
  bezirk: string;
  nr: number;
  bezirkName: string;
  /** Der Gremiumseintrag — fehlt, wenn das Verzeichnis die BV nicht führt. */
  gremium?: startseite.GremiumEintrag;
  vorsitz?: string;
  naechsteSitzung?: ris.SitzungsVerweis;
}

/**
 * Die Bezirksvertretungen in der amtlichen Nummernfolge der Bezirke —
 * Zuordnung über den Bezirks-Slug, nie über den Namen. Vorsitz aus dem
 * Sitzplan (`bezirksbuergermeister`), nächste Sitzung aus den Kennzahlen.
 */
export function bvZeilen(
  gremien: startseite.GremiumEintrag[] | undefined,
  sv: startseite.SitzverteilungDokument | undefined,
  kennzahlen: ris.GremienKennzahlenDokument | undefined,
): BvZeile[] {
  return BEZIRKE.map((b) => {
    const gremium = (gremien ?? []).find(
      (g) => g.typ === TYP_BEZIRKSVERTRETUNG && g.bezirk === b.slug,
    );
    return {
      bezirk: b.slug,
      nr: b.nr,
      bezirkName: b.name,
      gremium,
      vorsitz: sv?.bezirksbuergermeister?.find((x) => x.bezirk === b.slug)?.name,
      naechsteSitzung: gremium ? kennzahlenVon(kennzahlen, gremium.id)?.naechsteSitzung : undefined,
    };
  });
}
